import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ShoppingCart, X, Loader2 } from 'lucide-react'
import { SalesProduct, SalesPricingOption } from '../../types'

/**
 * Form fields the user supplies before we POST a create_media_buy. The
 * Campaign page combines these with `products`, the URL `campaign_id` and
 * the fixed `workflow: 'agentic_buy'` to build the final API payload.
 */
export interface CreateMediaBuyFormValues {
  budget: string
  brand_id: string
  domain: string
  pricing_option_id: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  /** Caller passes the products the user just ticked in the Media Buy tab. */
  selectedProducts: SalesProduct[]
  /** Disable the submit button while the parent's API call is in flight. */
  isSubmitting?: boolean
  /** Pre-fill values (used for the "remember last submission" UX). */
  initialValues?: Partial<CreateMediaBuyFormValues>
  onSubmit: (values: CreateMediaBuyFormValues) => void
}

interface PricingChoice {
  pricing_option_id: string
  rate: number | null
  currency: string
  pricing_model: string
  is_fixed: boolean
  /** True when every selected product offers this pricing option. */
  supportedByAll: boolean
}

/**
 * Build the pricing dropdown items by intersecting / unioning options across
 * the products the user selected. We dedupe by pricing_option_id and keep
 * the first occurrence's currency/rate so the dropdown stays a flat list of
 * unique IDs (the BE only needs the id; we display rate + currency for UX).
 */
function buildPricingChoices(selectedProducts: SalesProduct[]): PricingChoice[] {
  if (selectedProducts.length === 0) return []

  const supportCount = new Map<string, number>()
  const firstSeen = new Map<string, SalesPricingOption>()

  for (const product of selectedProducts) {
    const seenInThisProduct = new Set<string>()
    for (const opt of product.pricing_options || []) {
      if (!opt?.pricing_option_id) continue
      if (seenInThisProduct.has(opt.pricing_option_id)) continue
      seenInThisProduct.add(opt.pricing_option_id)

      supportCount.set(
        opt.pricing_option_id,
        (supportCount.get(opt.pricing_option_id) || 0) + 1,
      )
      if (!firstSeen.has(opt.pricing_option_id)) firstSeen.set(opt.pricing_option_id, opt)
    }
  }

  const total = selectedProducts.length
  return Array.from(firstSeen.entries()).map(([id, opt]) => ({
    pricing_option_id: id,
    rate: opt.rate,
    currency: opt.currency,
    pricing_model: opt.pricing_model,
    is_fixed: opt.is_fixed,
    supportedByAll: (supportCount.get(id) || 0) === total,
  }))
}

const numericPattern = /^\d+(\.\d+)?$/

export function CreateMediaBuyModal({
  isOpen,
  onClose,
  selectedProducts,
  isSubmitting = false,
  initialValues,
  onSubmit,
}: Props) {
  const pricingChoices = useMemo(
    () => buildPricingChoices(selectedProducts),
    [selectedProducts],
  )

  const [budget, setBudget] = useState(initialValues?.budget ?? '')
  const [brandId, setBrandId] = useState(initialValues?.brand_id ?? '')
  const [domain, setDomain] = useState(initialValues?.domain ?? '')
  const [pricingOptionId, setPricingOptionId] = useState(
    initialValues?.pricing_option_id ?? pricingChoices[0]?.pricing_option_id ?? '',
  )
  const [touched, setTouched] = useState(false)

  // Reset / re-seed every time the modal opens. This way the user always
  // sees their last submission while the parent decides what to remember.
  useEffect(() => {
    if (!isOpen) return
    setBudget(initialValues?.budget ?? '')
    setBrandId(initialValues?.brand_id ?? '')
    setDomain(initialValues?.domain ?? '')
    setPricingOptionId(
      initialValues?.pricing_option_id ?? pricingChoices[0]?.pricing_option_id ?? '',
    )
    setTouched(false)
  }, [isOpen, initialValues, pricingChoices])

  // If the chosen pricing option disappears (e.g. user deselects products
  // before opening the modal again), fall back to the first available one.
  useEffect(() => {
    if (!pricingOptionId) return
    const stillThere = pricingChoices.some(p => p.pricing_option_id === pricingOptionId)
    if (!stillThere) setPricingOptionId(pricingChoices[0]?.pricing_option_id ?? '')
  }, [pricingChoices, pricingOptionId])

  const budgetValid = numericPattern.test(budget.trim()) && Number(budget) > 0
  const brandIdValid = brandId.trim().length > 0
  const domainValid = domain.trim().length > 0
  const pricingValid = pricingOptionId.length > 0
  const canSubmit = budgetValid && brandIdValid && domainValid && pricingValid && !isSubmitting

  const submit = () => {
    setTouched(true)
    if (!canSubmit) return
    onSubmit({
      budget: String(Number(budget.trim())), // normalise '50000.00' -> '50000'
      brand_id: brandId.trim(),
      domain: domain.trim(),
      pricing_option_id: pricingOptionId,
    })
  }

  const fieldClass =
    'w-full rounded-lg border bg-white dark:bg-gray-900/60 text-sm px-3 py-2 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'

  const errorClass = 'mt-1 text-[11px] text-red-500 dark:text-red-400'

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[210] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white w-full max-w-lg overflow-hidden"
          >
            <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-800">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                  <ShoppingCart className="h-3 w-3" />
                  Media Buy
                </div>
                <h2 className="text-lg font-bold mt-1">Create media buy</h2>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {selectedProducts.length} product{selectedProducts.length === 1 ? '' : 's'} selected.
                  Provide the placement details below.
                </p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="h-8 w-8 flex-shrink-0 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/80 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white flex items-center justify-center transition-colors"
              >
                <X className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                  Budget
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={budget}
                  onChange={e => setBudget(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder="50000"
                  className={`${fieldClass} ${
                    touched && !budgetValid
                      ? 'border-red-500 focus:ring-red-500/40'
                      : 'border-gray-200 dark:border-gray-800'
                  }`}
                />
                {touched && !budgetValid && (
                  <p className={errorClass}>Enter a positive number (e.g. 50000).</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                    Brand ID
                  </label>
                  <input
                    type="text"
                    value={brandId}
                    onChange={e => setBrandId(e.target.value)}
                    onBlur={() => setTouched(true)}
                    placeholder="brand123"
                    className={`${fieldClass} ${
                      touched && !brandIdValid
                        ? 'border-red-500 focus:ring-red-500/40'
                        : 'border-gray-200 dark:border-gray-800'
                    }`}
                  />
                  {touched && !brandIdValid && (
                    <p className={errorClass}>Brand ID is required.</p>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                    Domain
                  </label>
                  <input
                    type="text"
                    value={domain}
                    onChange={e => setDomain(e.target.value)}
                    onBlur={() => setTouched(true)}
                    placeholder="example.com"
                    className={`${fieldClass} ${
                      touched && !domainValid
                        ? 'border-red-500 focus:ring-red-500/40'
                        : 'border-gray-200 dark:border-gray-800'
                    }`}
                  />
                  {touched && !domainValid && (
                    <p className={errorClass}>Domain is required.</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                  Pricing option
                </label>
                {pricingChoices.length === 0 ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    The selected product{selectedProducts.length === 1 ? '' : 's'} do not
                    expose any pricing options. Pick at least one product with pricing
                    before creating a media buy.
                  </p>
                ) : (
                  <select
                    value={pricingOptionId}
                    onChange={e => setPricingOptionId(e.target.value)}
                    className={`${fieldClass} appearance-none border-gray-200 dark:border-gray-800`}
                  >
                    {pricingChoices.map(p => {
                      const rate = p.rate != null ? `${p.rate} ${p.currency}` : 'rate n/a'
                      const tail = p.supportedByAll ? '' : ' (partial)'
                      return (
                        <option key={p.pricing_option_id} value={p.pricing_option_id}>
                          {p.pricing_option_id} — {rate}{tail}
                        </option>
                      )
                    })}
                  </select>
                )}
                {pricingChoices.some(p => !p.supportedByAll) && pricingChoices.length > 0 && (
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    "(partial)" means the option isn&apos;t offered by every selected product.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-3.5 w-3.5" />
                    Create media buy
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
